import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSocket } from '../contexts/SocketContext';

const POLL_INTERVAL_MS = 30000;

const HalqaBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const socket = useSocket();
  const [rooms, setRooms] = useState([]);

  const fetchRooms = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/halqa/active');
      setRooms(data.rooms || []);
    } catch {
      // Silently fail — banner just won't show if API is down
    }
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    fetchRooms();
    const intervalId = setInterval(fetchRooms, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchRooms]);

  // Real-time socket updates
  useEffect(() => {
    if (!socket) return;

    const handleNew = (room) => {
      setRooms((prev) => {
        const exists = prev.some((r) => r.id === room.id);
        return exists ? prev : [room, ...prev];
      });
    };

    const handleUpdated = (room) => {
      setRooms((prev) => prev.map((r) => (r.id === room.id ? room : r)));
    };

    const handleEnded = ({ roomId }) => {
      setRooms((prev) => prev.filter((r) => r.id !== roomId));
    };

    socket.on('halqa:new', handleNew);
    socket.on('halqa:updated', handleUpdated);
    socket.on('halqa:ended', handleEnded);

    return () => {
      socket.off('halqa:new', handleNew);
      socket.off('halqa:updated', handleUpdated);
      socket.off('halqa:ended', handleEnded);
    };
  }, [socket]);

  if (rooms.length === 0) return null;

  return (
    <div className="w-full border-b border-zinc-100 dark:border-zinc-800/80 bg-white dark:bg-black py-3 px-3">
      <div className="flex items-center gap-1.5 mb-2 px-0.5">
        {/* Pulsing live indicator */}
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
        </span>
        <span className="text-xs font-semibold text-rose-500 uppercase tracking-wider">
          {t('halqa.liveHalqas', 'Live Halqas')}
        </span>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
        {rooms.map((room) => (
          <button
            key={room.id}
            type="button"
            onClick={() => navigate(`/halqa/${room.id}`)}
            className="flex-shrink-0 flex flex-col gap-1.5 w-52 p-3 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900 hover:border-rose-400 dark:hover:border-rose-500 hover:bg-rose-50/40 dark:hover:bg-rose-950/20 transition-all text-left group"
          >
            <div className="flex items-center gap-1.5">
              <span className="text-base">🎙️</span>
              {/* Pulsing dot per card */}
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
              </span>
              <span className="text-[11px] font-semibold text-rose-500 uppercase tracking-wide">{t('halqa.liveHalqa', 'Live')}</span>
            </div>

            <p className="font-semibold text-sm text-zinc-900 dark:text-zinc-100 line-clamp-2 leading-snug">
              {room.title}
            </p>

            <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
              @{room.hostHandle}
            </p>

            <div className="flex items-center justify-between mt-0.5">
              <span className="text-xs text-zinc-400 dark:text-zinc-500">
                🎧 {room.listenersCount + (room.speakers?.length || 0)}
              </span>
              <span className="text-xs font-semibold text-rose-500 group-hover:underline">
                {t('halqa.joinStage', 'Join Stage')} →
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default HalqaBanner;
